<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { toast } from "@/common/utils/toast";

import { platformList } from "@/common/api/rest/platform";
import { groupList } from "@/common/api/rest/group";
import {
  collectionPlatformList,
  collectionPlatformCreate,
  collectionPlatformDel,
} from "@/common/api/rest/collection-platform";

import type { GroupType } from "@/common/types/group";
import type { CollectionType } from "@/common/types/collection";

import { baseLoading } from "@/common/components";

interface ListType {
  [key: string]: {
    item_id: string | null;
    platform_id: string;
    name: string;
    selected: boolean;
    groups: {
      [key: string]: {
        item_id: string | null;
        platform_id: string;
        group_id: string;
        name: string;
        selected: boolean;
      };
    } | null;
  };
}

const { t } = useI18n();

const props = defineProps<{
  collection: CollectionType;
}>();

const search = ref<string>("");
const list = ref<ListType>({});
const loading = ref(false);

async function fetchGroup(platform_id: string) {
  await groupList(platform_id, {
    full: true,
  }).then((response) => {
    const { success, data, message } = response;

    if (success) {
      data.list?.forEach(async (item: GroupType) => {
        list.value = {
          ...list.value,
          [platform_id]: {
            ...list.value[platform_id],
            groups: {
              ...list.value[platform_id].groups,
              [item.group_id]: {
                platform_id: platform_id,
                group_id: item.group_id,
                name: item.name,
                item_id: null,
                selected: false,
              },
            },
          },
        };
      });
    } else {
      toast({ message: message, type: "error" });
    }
  });
}

async function fetchData() {
  await platformList({
    full: true,
    search: search.value,
  })
    .then(async (response) => {
      const { success, data, message } = response;

      if (success) {
        for (const item of data.list || []) {
          list.value = {
            ...list.value,
            [item.platform_id]: {
              platform_id: item.platform_id,
              name: item.name,
              selected: false,
              item_id: null,
              groups: null,
            },
          };
          await fetchGroup(item.platform_id);
        }
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function fetchCollectionPlatforms() {
  await collectionPlatformList(props.collection.collection_id, {
    full: true,
    search: search.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        data.list?.forEach((item: any) => {
          list.value[item.platform_id].item_id = item.item_id;
          list.value[item.platform_id].selected = true;

          if (list.value[item.platform_id].groups?.[item.group_id]) {
            list.value[item.platform_id].groups[item.group_id].item_id =
              item.item_id;
            list.value[item.platform_id].groups[item.group_id].selected = true;
          }
        });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function addCollectionPlatform(
  platform_id: string,
  group_id: string | null = null
) {
  const payload = {
    platform_id: platform_id,
    group_id: group_id,
  };

  await collectionPlatformCreate(props.collection.collection_id, payload)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchCollectionPlatforms();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function removeCollectionPlatform(item_id: string) {
  await collectionPlatformDel(item_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchCollectionPlatforms();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function togglePlatform(data: {
  item_id: string;
  platform_id: string;
  selected: boolean;
}) {
  if (data.selected) {
    addCollectionPlatform(data.platform_id);
  } else {
    removeCollectionPlatform(data.item_id);
  }
}

function toggleGroup(data: {
  item_id: string;
  platform_id: string;
  group_id: string;
  selected: boolean;
}) {
  if (data.selected) {
    addCollectionPlatform(data.platform_id, data.group_id);
  } else {
    removeCollectionPlatform(data.item_id);
  }
}

onMounted(async () => {
  loading.value = true;
  await fetchData();
  await fetchCollectionPlatforms();
  loading.value = false;
});
</script>

<template>
  <section class="tab-collection">
    <div class="content">
      <template v-if="loading">
        <base-loading />
      </template>
      <template v-else>
        <div class="list">
          <template v-for="(item, index) in list" :key="index">
            <div class="item">
              <div class="platform">
                <template v-if="!item.groups">
                  <div class="custom-checkbox">
                    <input
                      type="checkbox"
                      v-model="item.selected"
                      :name="item.name"
                      :id="item.platform_id"
                      @change="togglePlatform(item)"
                    />
                    <label :for="item.platform_id" class="checkmark"></label>

                    <label :for="item.platform_id" class="platform-label">
                      {{ item.name }}
                    </label>
                  </div>
                </template>
                <template v-else>
                  <label class="platform-label">
                    {{ item.name }}
                  </label>
                </template>
              </div>

              <template v-if="item.groups">
                <div class="groups">
                  <template v-for="(group, index) in item.groups" :key="index">
                    <div class="group">
                      <div class="custom-checkbox">
                        <input
                          type="checkbox"
                          v-model="group.selected"
                          :name="group.name"
                          :id="group.group_id"
                          @change="toggleGroup(group)"
                        />
                        <label :for="group.group_id" class="checkmark"></label>

                        <label :for="group.group_id" class="group-label">
                          {{ group.name }}
                        </label>
                      </div>
                    </div>
                  </template>
                </div>
              </template>
            </div>
          </template>
        </div>
      </template>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.tab-collection {
  display: flex;
  flex-direction: column;
  padding-block: 1rem;
  gap: 1rem;

  .content {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .list {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      .item {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        border-radius: 0.5rem;
        background: var(--white-off);
        padding-block: 1rem;
        padding-inline: 1rem;

        .platform {
          display: flex;
          flex-direction: column;
          gap: 1rem;

          .platform-label {
            font-size: 1rem;
            font-weight: bold;
            cursor: pointer;
          }
        }

        .groups {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          padding-left: 1rem;

          .group-label {
            font-size: 1rem;
            cursor: pointer;
          }
        }
      }
    }

    .custom-checkbox {
      display: inline-flex;
      align-items: center;
      cursor: pointer;
      user-select: none;

      input[type="checkbox"] {
        display: none;

        &:checked + .checkmark {
          background-color: var(--primary);
          border-color: var(--primary);

          &::after {
            display: block;
          }
        }
      }

      .checkmark {
        width: 1.25rem;
        height: 1.25rem;
        border: 1px solid var(--primary);
        border-radius: 0.25rem;
        margin-right: 0.5rem;
        position: relative;
        background-color: #fff;
        transition: all 0.2s ease;

        &::after {
          content: "";
          position: absolute;
          left: 35%;
          top: 2px;
          width: 5px;
          height: 10px;
          border: solid white;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }
      }
    }
  }
}

@media screen and (max-width: 1024px) {
  .tab-collection {
    .content {
      padding-inline: 0;
    }
  }
}
</style>
